/**
 * notification-display.ts
 *
 * Shared display helpers for the legacy `notifications.typeID` catalog
 * (typeID 1–26). Used by both the Alerts page (NotificationsPage.tsx)
 * and the bottom-right pop-up toaster.
 *
 * The backend `/notifications/pop` endpoint already returns a fully-
 * decorated `display` block (title, body, group, link) so the popup hook
 * can render directly. These helpers exist for the Alerts list page,
 * which still fetches raw `Notification` rows.
 */
import type { NavigateFunction } from 'react-router-dom';
import type { Notification } from '@/services/notifications.service';
import { notificationModal } from '@/store/notificationModalStore';

/** Family classifications used for accent colors. */
export type NotificationGroup =
  | 'lead'
  | 'email'
  | 'sms'
  | 'message'
  | 'payment'
  | 'appt'
  | 'clinical'
  | 'note'
  | 'eform'
  | 'report'
  | 'unknown';

interface TypeConfig {
  title: string;
  group: NotificationGroup;
}

/**
 * Per-typeID display config matching the rendering in legacy
 * `notification_display.php` and the server-side `decorateForPopup`
 * helper in `api/app/Http/Controllers/V5/NotificationsController.php`.
 */
export const NOTIFICATION_TYPE_CONFIG: Record<number, TypeConfig> = {
  1:  { title: 'New lead received',                group: 'lead' },
  2:  { title: 'New email from patient',           group: 'email' },
  // Type 3 also carries inbound WhatsApp, whose title says "New WhatsApp
  // message from ...". That wording cannot be derived here — it depends on the
  // linked message's channel, which is not on the raw row — so the server sends
  // it in `display.title`, and both surfaces prefer that over this fallback
  // (NotificationsPage renders `n.display?.title || notificationTitle(n)`).
  // Do not "correct" this string to mention WhatsApp; it is the channel-unknown
  // fallback only.
  3:  { title: 'New SMS from patient',             group: 'sms' },
  4:  { title: 'New message received',             group: 'message' },
  5:  { title: 'Payment declined',                 group: 'payment' },
  6:  { title: 'Appointment confirmed',            group: 'appt' },
  7:  { title: 'Appointment cancelled',            group: 'appt' },
  8:  { title: 'Appointment checked in',           group: 'appt' },
  9:  { title: 'Appointment checked back',         group: 'appt' },
  10: { title: 'Appointment checked out',          group: 'appt' },
  11: { title: 'Lab results received',             group: 'clinical' },
  12: { title: 'Payment accepted',                 group: 'payment' },
  13: { title: 'Consult ready for review',         group: 'clinical' },
  14: { title: 'Q/A ready for review',             group: 'clinical' },
  15: { title: 'Appointment reschedule requested', group: 'appt' },
  16: { title: 'New note',                         group: 'note' },
  17: { title: 'Medical clearance approved',       group: 'clinical' },
  18: { title: 'E-form received',                  group: 'eform' },
  19: { title: 'Surgeon note updated',             group: 'clinical' },
  20: { title: 'Payment disputed',                 group: 'payment' },
  21: { title: 'Lead assigned to you',             group: 'lead' },
  22: { title: 'Report ready',                     group: 'report' },
  // 23 shipped with a filter-group entry below but no config entry, so every
  // pharmacy-support alert rendered as a bare "Notification" in the unknown
  // colour. SCRUM-1805.
  23: { title: 'Pharmacy support',                 group: 'message' },
  24: { title: 'Order needs attention',            group: 'payment' },
  25: { title: 'Vibrant order not confirmed',      group: 'clinical' },
  26: { title: 'Membership cancelled',             group: 'payment' },
};

/** Type-aware filter groups used by the Alerts page select dropdown. */
export const NOTIFICATION_TYPE_GROUPS: Array<{ label: string; typeIds: number[] | null }> = [
  { label: 'All Types',          typeIds: null },
  { label: 'Lead notifications', typeIds: [1, 21] },
  { label: 'Email/SMS',          typeIds: [2, 3] },
  { label: 'Messages',           typeIds: [4] },
  { label: 'Payments',           typeIds: [5, 12, 20] },
  { label: 'Appointments',       typeIds: [6, 7, 8, 9, 10, 15] },
  { label: 'Clinical',           typeIds: [11, 13, 14, 17, 18, 19] },
  { label: 'Notes',              typeIds: [16] },
  { label: 'Reports',            typeIds: [22] },
  { label: 'Pharmacy support',   typeIds: [23] },
  { label: 'Order issues',       typeIds: [24] },
  { label: 'Lab orders',         typeIds: [25] },
  { label: 'Memberships',        typeIds: [26] },
];

export function notificationTitle(n: Notification): string {
  if (n.typeId && NOTIFICATION_TYPE_CONFIG[n.typeId]) {
    return NOTIFICATION_TYPE_CONFIG[n.typeId].title;
  }
  return 'Notification';
}

export function notificationGroup(typeId: number | null | undefined): NotificationGroup {
  if (typeId && NOTIFICATION_TYPE_CONFIG[typeId]) {
    return NOTIFICATION_TYPE_CONFIG[typeId].group;
  }
  return 'unknown';
}

/**
 * Action descriptor produced by `notificationAction`. Tells a caller whether
 * to open an in-app modal (preferred when one exists in the active UX) or
 * do a regular route navigation. Routing decision matches what staff see
 * when clicking the same record elsewhere in the app.
 */
export type NotificationAction =
  | { kind: 'openLeadModal'; leadId: number }
  | { kind: 'openPaymentModal'; patientId: number; paymentId: number }
  | { kind: 'openAppointmentModal'; appointmentId: number }
  | { kind: 'openNoteModal'; patientId: number; noteId: number }
  | { kind: 'openSurveyResponseModal'; patientId: number; responseId: number }
  | { kind: 'navigate'; path: string }
  | null;

/**
 * Decide what should happen when the user clicks a notification (popup or
 * Alerts list row). Modal targets reuse the same detail modal the rest of
 * the app opens for that record; everything else falls back to a normal
 * route navigation.
 *
 *   typeID  | target
 *   1, 21   | LeadDetailModal
 *   5,12,20 | RecordPatientPaymentModal (the unified Payment Details modal)
 *   6-10,15 | UnifiedAppointmentModal (the unified Appointment Details modal)
 *   16      | NoteViewModal
 *   18      | SurveyResponseModal
 *   2,3,4   | navigate → patient detail (the comm log lives on that page)
 *   11      | navigate → patient detail (labs tab)
 *  13,17,19 | navigate → the plan's Surgery section (consult / clearance / surgeon)
 *   14      | navigate → the plan's Post-ops section, or the patient's QA tab
 *           |   when the QA page raised it (it stamps an author; Post-ops does not)
 *   23      | navigate → /peptides?ticketOrder=<orderId> (opens the ticket), else patient detail
 *   24      | navigate → /peptides (the order list, where retry/cancel live)
 *   25      | navigate → the patient's Labs section (the Vibrant order and Send again)
 *   26      | navigate → the patient's Memberships section (a membership was cancelled)
 */
export function notificationAction(n: Notification): NotificationAction {
  const t = n.typeId;
  if (!t) return null;

  // Lead notifications → Lead Details modal.
  if ((t === 1 || t === 21) && n.contactId) {
    return { kind: 'openLeadModal', leadId: n.contactId };
  }

  // Payment notifications → Record / Payment Details modal.
  if ((t === 5 || t === 12 || t === 20) && n.patientId && n.paymentId) {
    return {
      kind: 'openPaymentModal',
      patientId: n.patientId,
      paymentId: n.paymentId,
    };
  }

  // Appointment notifications → Unified Appointment modal.
  if ((t === 6 || t === 7 || t === 8 || t === 9 || t === 10 || t === 15) && n.apptId) {
    return { kind: 'openAppointmentModal', appointmentId: n.apptId };
  }

  // New Note → NoteViewModal. typeID 16 stores noteID in contactID.
  if (t === 16 && n.patientId && n.contactId) {
    return { kind: 'openNoteModal', patientId: n.patientId, noteId: n.contactId };
  }

  // E-form received → SurveyResponseModal. typeID 18 stores responseID
  // in contactID.
  if (t === 18 && n.patientId && n.contactId) {
    return {
      kind: 'openSurveyResponseModal',
      patientId: n.patientId,
      responseId: n.contactId,
    };
  }

  // Messages (2, 3, 4) → patient detail (comm log).
  if ((t === 2 || t === 3 || t === 4) && n.patientId) {
    return { kind: 'navigate', path: `/patients/${n.patientId}` };
  }

  // Lab results → patient detail (labs tab).
  if (t === 11 && n.patientId) {
    return { kind: 'navigate', path: `/patients/${n.patientId}` };
  }

  // Consult ready / Clearance / Surgeon note are Surgery alerts → the plan's
  // Surgery section, the same page the Surgery worklist opens. Both the V5
  // writer and legacy's consult_update_*.php set patientID; the /plans form
  // only covers a row that lost it (PlanRedirect keeps the section).
  if ((t === 13 || t === 17 || t === 19) && n.planId) {
    const path = n.patientId
      ? `/patients/${n.patientId}/plans/${n.planId}?section=surgery`
      : `/plans/${n.planId}?section=surgery`;
    return { kind: 'navigate', path };
  }

  // Q/A ready for review has two writers, told apart by author. The QA page
  // (QaFollowersController, a case assigned to someone) stamps the assigning
  // user; its cases live on the patient's Quality Assurance tab. Post-ops
  // reopening a case (PostOpsNotificationRules, legacy
  // postop_update_qaresolved.php) writes author 0 and belongs on the plan's
  // Post-ops section, where legacy links it.
  if (t === 14 && n.authorId && n.patientId) {
    return { kind: 'navigate', path: `/patients/${n.patientId}?section=qa` };
  }
  if (t === 14 && !n.authorId && n.planId) {
    const path = n.patientId
      ? `/patients/${n.patientId}/plans/${n.planId}?section=post-ops`
      : `/plans/${n.planId}?section=post-ops`;
    return { kind: 'navigate', path };
  }

  // A pharmacy support reply or ticket status move → that order's ticket, open
  // on the Axiom page. contactId holds the integrationOrderId (Robin on #1200:
  // the alert used to land on the patient's details page, nowhere near the
  // ticket). Alerts written before the id was stored keep the patient page.
  if (t === 23 && n.contactId) {
    return { kind: 'navigate', path: `/peptides?ticketOrder=${n.contactId}` };
  }
  if (t === 23 && n.patientId) {
    return { kind: 'navigate', path: `/patients/${n.patientId}` };
  }

  // An order stopped before reaching the pharmacy → the account-wide Peptides
  // list, which is where staff triage orders and where the retry and cancel
  // actions live. The patient page would open on the wrong tab. Matches the
  // link built in NotificationsController so both surfaces agree.
  if (t === 24) {
    return { kind: 'navigate', path: '/peptides' };
  }

  // Vibrant has not confirmed a lab order → the patient's Labs section, where
  // the order and its Send again action live. Matches the link built in
  // NotificationsController so both surfaces agree.
  if (t === 25 && n.patientId) {
    return { kind: 'navigate', path: `/patients/${n.patientId}?section=labs` };
  }

  // A membership was cancelled → the patient's Memberships section, to review
  // the stop date and reason. Matches NotificationsController's link.
  if (t === 26 && n.patientId) {
    return { kind: 'navigate', path: `/patients/${n.patientId}?section=memberships` };
  }

  // Report ready → the stored report result. typeID 22 stores the
  // reportsCustomResultsID in contactID (set by the legacy cron).
  if (t === 22 && n.contactId) {
    return { kind: 'navigate', path: `/reports/results/${n.contactId}?type=cst` };
  }

  // Payment notification with patient but no paymentId resolved → patient
  // payments tab.
  if ((t === 5 || t === 12 || t === 20) && n.patientId) {
    return { kind: 'navigate', path: `/patients/${n.patientId}` };
  }

  // Appointment notification with no apptId → patient scheduler.
  if ((t === 6 || t === 7 || t === 8 || t === 9 || t === 10 || t === 15) && n.patientId) {
    return { kind: 'navigate', path: `/patients/${n.patientId}` };
  }

  return null;
}

/** The patient name carried on an alert row, or null when it has none. */
export function notificationPatientName(n: Notification): string | null {
  if (n.patientFirstName || n.patientLastName) {
    return [n.patientFirstName, n.patientLastName].filter(Boolean).join(' ');
  }
  return null;
}

export interface OpenNotificationDeps {
  navigate: NavigateFunction;
  /** Records the patient in the recent-history sidebar. */
  visitPatient: (patient: { patientId: number; name: string }) => void;
  /** Marks the alert read. The caller decides what else happens (local state, count refresh). */
  markRead: (id: number) => Promise<void>;
}

/**
 * What a click on an alert row does — shared by the Alerts page and the
 * Dashboard Alerts card so the two behave the same: start marking it read
 * (when unread), record the patient in recent history when the target is a
 * patient page, then open the target straight away.
 *
 * Returns the mark-read promise, so a caller can refresh the unread count only
 * after the write has landed — a refetch that races it reads the old number.
 */
export function openNotification(n: Notification, deps: OpenNotificationDeps): Promise<void> {
  const read = n.isRead ? Promise.resolve() : deps.markRead(n.notificationId);

  const action = notificationAction(n);
  if (action?.kind === 'navigate' && n.patientId && action.path.startsWith('/patients/')) {
    deps.visitPatient({
      patientId: n.patientId,
      name: notificationPatientName(n) || `#${n.patientId}`,
    });
  }
  runNotificationAction(n, deps.navigate);

  return read;
}

/**
 * Single dispatcher both the popup hook and the Alerts page run when the
 * user clicks a notification. Keeps the modal-vs-navigate decision in one
 * place so adding a new modal target requires touching only this file plus
 * `NotificationModalHost`.
 *
 * @returns true when an action was dispatched, false when the click was a
 *          no-op (no actionable target on the notification).
 */
export function runNotificationAction(
  n: Notification,
  navigate: NavigateFunction,
): boolean {
  const action = notificationAction(n);
  if (!action) return false;

  switch (action.kind) {
    case 'openLeadModal':
      notificationModal.open({ kind: 'lead', leadId: action.leadId });
      return true;
    case 'openPaymentModal':
      notificationModal.open({
        kind: 'payment',
        patientId: action.patientId,
        paymentId: action.paymentId,
      });
      return true;
    case 'openAppointmentModal':
      notificationModal.open({
        kind: 'appointment',
        appointmentId: action.appointmentId,
      });
      return true;
    case 'openNoteModal':
      notificationModal.open({
        kind: 'note',
        patientId: action.patientId,
        noteId: action.noteId,
      });
      return true;
    case 'openSurveyResponseModal':
      notificationModal.open({
        kind: 'surveyResponse',
        patientId: action.patientId,
        responseId: action.responseId,
      });
      return true;
    case 'navigate':
      navigate(action.path);
      return true;
  }
}

/**
 * @deprecated Prefer `runNotificationAction`. Kept as a thin shim so any
 * legacy importer keeps working; the Alerts page and popup hook now route
 * through the action discriminator.
 */
export function notificationLink(n: Notification): string | null {
  const action = notificationAction(n);
  if (!action) return null;
  if (action.kind === 'navigate') return action.path;
  return null;
}

/**
 * Tailwind container classes for the bottom-right toast, keyed by the
 * notification group. Designed to echo the inline `<strong style="color:...">`
 * colors in the legacy `notification_display.php`.
 */
export const NOTIFICATION_GROUP_STYLES: Record<NotificationGroup, { container: string; accent: string }> = {
  lead:     { container: 'border-blue-200 bg-blue-50',     accent: 'text-blue-700' },
  email:    { container: 'border-amber-200 bg-amber-50',   accent: 'text-amber-700' },
  sms:      { container: 'border-rose-200 bg-rose-50',     accent: 'text-rose-700' },
  message:  { container: 'border-indigo-200 bg-indigo-50', accent: 'text-indigo-700' },
  payment:  { container: 'border-red-200 bg-red-50',       accent: 'text-red-700' },
  appt:     { container: 'border-green-200 bg-green-50',   accent: 'text-green-700' },
  clinical: { container: 'border-amber-200 bg-amber-50',   accent: 'text-amber-800' },
  note:     { container: 'border-red-200 bg-red-50',       accent: 'text-red-700' },
  eform:    { container: 'border-orange-200 bg-orange-50', accent: 'text-orange-700' },
  report:   { container: 'border-teal-200 bg-teal-50',     accent: 'text-teal-700' },
  unknown:  { container: 'border-slate-200 bg-slate-50',   accent: 'text-slate-700' },
};
